import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { RichText, Text } from "@sitecore-jss/sitecore-jss-react";
import { mobileIs, getOverallSettingsClasses, loadScrollMagic, componentId } from "../../utils/siteUtils";
import "./style.scss";
import Swiper from 'swiper';
import gsap from 'gsap';
import { gtag } from "../../utils";
const HighlightSlider = props => {
	const { lang, rendering, fields, params, page } = props;
	const isAR = page?.fields?.rtl?.value;
	const { list, m_text, m_title, pc_text, pc_title, paddingAboveTop } = fields;
	const { infinitely, duration } = params;
	const isMobile = mobileIs()
	const swiperEl = useRef(null)
	const swiper = useRef(null)
	const rootNode = useRef()
	const [currentIndex, setCurrentIndex] = useState((infinitely === '1' || isMobile) ? 0 : 1)
	const wrapperRef = useRef(null);
	const progressBarRef = useRef(null);
	const prevBtnRef = useRef(null);
	const nextBtnRef = useRef(null);
	const totalSlides = list.length;
	const [isPrevDisabled, setIsPrevDisabled] = useState(true);
	const [isNextDisabled, setIsNextDisabled] = useState(false);
	const [imagesLoaded, setImagesLoaded] = useState(false);
	const handleImagesLoad = () => {
		const images = document.querySelectorAll('.slide-image');
		if (!images.length) return setImagesLoaded(true);

		let loadedCount = 0;
		const total = images.length;

		const onImageLoad = () => {
			loadedCount++;
			if (loadedCount === total) {
				setImagesLoaded(true);
			}
		};

		images.forEach(img => {
			if (img.complete) {
				onImageLoad();
			} else {
				img.addEventListener('load', onImageLoad);
				img.addEventListener('error', onImageLoad);
			}
		});

		return () => {
			images.forEach(img => {
				img.removeEventListener('load', onImageLoad);
				img.removeEventListener('error', onImageLoad);
			});
		};
	};
	const buildSwiper = () => {
		if (!swiperEl.current || !list.length) return;

		if (swiper.current) {
			swiper.current.destroy();
		}

		const options = {
			watchSlidesProgress: true,
			slidesPerView: 'auto',
			spaceBetween: isMobile ? -10 : -26,
			loop: true,
			loopedSlides: list.length,
			centeredSlides: true,
			navigation: {
				nextEl: ".swiper-button-next",
				prevEl: ".swiper-button-prev",
			},
			on: {
				init(swiper) {
					swiper.update();
				},
				transitionEnd(swiper) {
					setCurrentIndex(swiper.realIndex);
				}
			}
		};

		swiper.current = new Swiper(swiperEl.current, options);
	};
	useEffect(() => {
		if (infinitely === '1') {
			const cleanup = handleImagesLoad();
			return cleanup;
		}
	}, [list]);

	useEffect(() => {
		if (swiperEl.current && list.length > 0 && imagesLoaded && infinitely === '1') {
			buildSwiper();
		}
	}, [list.length, isMobile, imagesLoaded]);

	useEffect(() => {
		if (infinitely !== '1' && totalSlides) {
			const options = {
				pagination: {
					el: ".controls-container .pagination",
					type: "progressbar",
				},
				navigation: {
					nextEl: nextBtnRef.current,
					prevEl: prevBtnRef.current,
				},
				watchSlidesProgress: true,
				slidesPerView: isMobile ? 1.05 : 2.22,
				slidesPerGroup: getSlidesPerGroup(totalSlides),
				spaceBetween: 8,
				on: {
					slideChange: function () {
						updateButtonStates(this);
					},
				}
			};
			swiper.current = new Swiper(swiperEl.current, options);
		}
	}, [currentIndex])
	const getSlidesPerGroup = (totalSlides) => {
		if (totalSlides > 3) {
			return isMobile ? 1 : 2;
		} else {
			return 1;
		}
	};
	const updateButtonStates = (swiper) => {
		const isFirstSlide = swiper.activeIndex === 0;
		const isLastSlide = swiper.isEnd;
		setIsPrevDisabled(isFirstSlide);
		setIsNextDisabled(isLastSlide);
	};
	const padZero = (num, length = 2) => {
		return String(num).padStart(length, '0');
	};
	const rootId = componentId(rendering);
	const rootClasses = classNames("highlight-slider", getOverallSettingsClasses(fields));
	if (infinitely === '1') {
		return (
			<div dir={isAR ? "rtl" : "ltr"} key={isAR ? "rtl" : "ltr"} className={`${rootClasses} loop`} datasourceid={rendering.dataSource} ref={rootNode} id={rootId}>
				{!isMobile &&
					(<>
						<RichText tag="div" className="pc-title" field={pc_title} />
						<RichText tag="div" className="pc-subTitle" field={pc_text} />
					</>)}
				{isMobile &&
					(<>
						<RichText tag="div" className="m-title" field={m_title} />
						<RichText tag="div" className="m-subTitle" field={m_text} />
					</>)}
				<div className="highlight-slider__swiper swiper" ref={swiperEl}>
					<div className="swiper-wrapper" ref={wrapperRef}>
						{list && list.map((item, index) => (
							<div className={"swiper-slide"} key={index}>
								<img src={item?.fields?.image?.value?.src} alt={item?.fields?.image?.value?.src} className="slide-image" />
								<div className="text-content">
									{!isMobile &&
										<RichText tag="div" className="title" field={item?.fields?.pc_text} id={`item__title${item.id}`} />}
									{isMobile &&
										<RichText tag="div" className="title" field={item?.fields?.m_text} id={`item__title${item.id}`} />}
								</div>
							</div>
						))}
					</div>
					<div className="controls-container">
						<div className="swiper-buttons">
							<div className="swiper-button swiper-button-prev"  >
								<svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
									<rect x="37" y="37" width="36" height="36" rx="18" transform="rotate(180 37 37)" fill="white" />
									<rect x="37" y="37" width="36" height="36" rx="18" transform="rotate(180 37 37)" stroke="black" stroke-width="1.125" />
									<path fill-rule="evenodd" clip-rule="evenodd" d="M15.7374 18.5576L20.4624 12.6113L21.2374 13.5887L16.9374 19.0013L21.2374 24.414L20.4624 25.3913L15.7374 19.445C15.5393 19.2 15.5393 18.8027 15.7374 18.5576Z" fill="white" />
									<path d="M20.6426 13.5879L16.5732 18.7119C16.4385 18.8815 16.4385 19.1215 16.5732 19.291L20.6436 24.4141L20.4619 24.6426L16.1016 19.1553L16.0996 19.1523L16.0801 19.1211C16.0406 19.041 16.0471 18.9157 16.0996 18.8506L16.1016 18.8477L20.4619 13.3594L20.6426 13.5879Z" stroke="black" stroke-width="0.931035" stroke-linejoin="round" />
								</svg>
							</div>
							<div className="custom-fraction">
								<span className="current-page">{padZero(currentIndex + 1)}</span>
								<span className="divider">/</span>
								<span className="total-pages">{padZero(totalSlides)}</span>
							</div>
							<div className="swiper-button swiper-button-next" >
								<svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
									<rect x="1" y="1" width="36" height="36" rx="18" fill="white" />
									<rect x="1" y="1" width="36" height="36" rx="18" stroke="black" stroke-width="1.125" />
									<path fill-rule="evenodd" clip-rule="evenodd" d="M22.2626 18.5576L17.5376 12.6113L16.7626 13.5887L21.0626 19.0013L16.7626 24.414L17.5376 25.3913L22.2626 19.445C22.4607 19.2 22.4607 18.8027 22.2626 18.5576Z" fill="white" />
									<path d="M17.3574 24.4121L21.4268 19.2881C21.5615 19.1185 21.5615 18.8785 21.4268 18.709L17.3564 13.5859L17.5381 13.3574L21.8984 18.8447L21.9004 18.8477L21.9199 18.8789C21.9594 18.959 21.9529 19.0843 21.9004 19.1494L21.8984 19.1523L17.5381 24.6406L17.3574 24.4121Z" stroke="black" stroke-width="0.931035" stroke-linejoin="round" />
								</svg>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
	return (
		<div className={rootClasses} dir={isAR ? "rtl" : "ltr"} key={isAR ? "rtl" : "ltr"} datasourceid={rendering.dataSource} ref={rootNode} id={rootId}>
			{!isMobile &&
				(<div className="title-wrapper">
					<RichText tag="div" className="pc-title" field={pc_title} />
					<RichText tag="div" className="pc-subTitle" field={pc_text} />
				</div>)}
			{isMobile &&
				(<div className="title-wrapper">
					<RichText tag="div" className="m-title" field={m_title} />
					<RichText tag="div" className="m-subTitle" field={m_text} />
				</div>)}
			<div className="highlight-slider__swiper swiper" ref={swiperEl}>
				<div className="swiper-wrapper" ref={wrapperRef}>
					{list && list.map((item, index) => (
						<div className={"swiper-slide"} key={index}>
							<div className="square-image-container">
								<img src={item?.fields?.image?.value?.src} alt={item?.fields?.image?.value?.src} className="slide-image" />
							</div>
							<div className="text-content">
								{!isMobile &&
									<RichText tag="div" className="title" field={item?.fields?.pc_text} id={`item__title${item.id}`} />}
								{isMobile &&
									<RichText tag="div" className="title" field={item?.fields?.m_text} id={`item__title${item.id}`} />}
							</div>
						</div>
					))}
				</div>
				<div className="controls-container">
					<div className="pagination">
					</div>
					<div className="swiper-buttons">
						{isMobile && <>
							<div ref={prevBtnRef} className={`swiper-button swiper-button-prev ${isPrevDisabled ? 'disabled' : ''}`} >
								<svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
									<rect x="37" y="37" width="36" height="36" rx="18" transform="rotate(180 37 37)" fill="white" />
									<rect x="37" y="37" width="36" height="36" rx="18" transform="rotate(180 37 37)" stroke="black" stroke-width="1.125" />
									<path fill-rule="evenodd" clip-rule="evenodd" d="M15.7374 18.5576L20.4624 12.6113L21.2374 13.5887L16.9374 19.0013L21.2374 24.414L20.4624 25.3913L15.7374 19.445C15.5393 19.2 15.5393 18.8027 15.7374 18.5576Z" fill="white" />
									<path d="M20.6426 13.5879L16.5732 18.7119C16.4385 18.8815 16.4385 19.1215 16.5732 19.291L20.6436 24.4141L20.4619 24.6426L16.1016 19.1553L16.0996 19.1523L16.0801 19.1211C16.0406 19.041 16.0471 18.9157 16.0996 18.8506L16.1016 18.8477L20.4619 13.3594L20.6426 13.5879Z" stroke="black" stroke-width="0.931035" stroke-linejoin="round" />
								</svg>
							</div>
							<div ref={nextBtnRef} className={`swiper-button swiper-button-next ${isNextDisabled ? 'disabled' : ''}`} >
								<svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
									<rect x="1" y="1" width="36" height="36" rx="18" fill="white" />
									<rect x="1" y="1" width="36" height="36" rx="18" stroke="black" stroke-width="1.125" />
									<path fill-rule="evenodd" clip-rule="evenodd" d="M22.2626 18.5576L17.5376 12.6113L16.7626 13.5887L21.0626 19.0013L16.7626 24.414L17.5376 25.3913L22.2626 19.445C22.4607 19.2 22.4607 18.8027 22.2626 18.5576Z" fill="white" />
									<path d="M17.3574 24.4121L21.4268 19.2881C21.5615 19.1185 21.5615 18.8785 21.4268 18.709L17.3564 13.5859L17.5381 13.3574L21.8984 18.8447L21.9004 18.8477L21.9199 18.8789C21.9594 18.959 21.9529 19.0843 21.9004 19.1494L21.8984 19.1523L17.5381 24.6406L17.3574 24.4121Z" stroke="black" stroke-width="0.931035" stroke-linejoin="round" />
								</svg>
							</div>
						</>}
						{!isMobile && <>
							<div ref={prevBtnRef} className={`swiper-button swiper-button-prev ${isPrevDisabled ? 'disabled' : ''}`}>
								<svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
									<rect x="37" y="37" width="36" height="36" rx="18" transform="rotate(180 37 37)" fill="white" />
									<rect x="37" y="37" width="36" height="36" rx="18" transform="rotate(180 37 37)" stroke="black" stroke-width="1.125" />
									<path fill-rule="evenodd" clip-rule="evenodd" d="M15.7374 18.5576L20.4624 12.6113L21.2374 13.5887L16.9374 19.0013L21.2374 24.414L20.4624 25.3913L15.7374 19.445C15.5393 19.2 15.5393 18.8027 15.7374 18.5576Z" fill="white" />
									<path d="M20.6426 13.5879L16.5732 18.7119C16.4385 18.8815 16.4385 19.1215 16.5732 19.291L20.6436 24.4141L20.4619 24.6426L16.1016 19.1553L16.0996 19.1523L16.0801 19.1211C16.0406 19.041 16.0471 18.9157 16.0996 18.8506L16.1016 18.8477L20.4619 13.3594L20.6426 13.5879Z" stroke="black" stroke-width="0.931035" stroke-linejoin="round" />
								</svg>
							</div>
							<div ref={nextBtnRef} className={`swiper-button swiper-button-next ${isNextDisabled ? 'disabled' : ''}`} >
								<svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
									<rect x="1" y="1" width="36" height="36" rx="18" fill="white" />
									<rect x="1" y="1" width="36" height="36" rx="18" stroke="black" stroke-width="1.125" />
									<path fill-rule="evenodd" clip-rule="evenodd" d="M22.2626 18.5576L17.5376 12.6113L16.7626 13.5887L21.0626 19.0013L16.7626 24.414L17.5376 25.3913L22.2626 19.445C22.4607 19.2 22.4607 18.8027 22.2626 18.5576Z" fill="white" />
									<path d="M17.3574 24.4121L21.4268 19.2881C21.5615 19.1185 21.5615 18.8785 21.4268 18.709L17.3564 13.5859L17.5381 13.3574L21.8984 18.8447L21.9004 18.8477L21.9199 18.8789C21.9594 18.959 21.9529 19.0843 21.9004 19.1494L21.8984 19.1523L17.5381 24.6406L17.3574 24.4121Z" stroke="black" stroke-width="0.931035" stroke-linejoin="round" />
								</svg>
							</div>
						</>}
					</div>
				</div>
			</div>
		</div>
	);
};

export default HighlightSlider;